import React, { useMemo, useState } from "react";
import { Trash2, X } from "lucide-react";
import { calculateOrderItemTotals } from "../../utils/salesUtils";

const formatInputAmount = (value) => {
  const amount = Math.max(0, Number(value) || 0);
  return amount > 0 ? Math.round(amount).toLocaleString("id-ID") : "";
};

export default function ItemAdjustmentModal({
  field,
  item,
  onClose,
  onSave,
}) {
  const isDiscount = field === "discount";
  const totals = useMemo(() => calculateOrderItemTotals(item), [item]);
  const initialAmount = isDiscount ? totals.discountAmount : totals.additionalCost;
  const [nominal, setNominal] = useState(formatInputAmount(initialAmount));
  const [percentage, setPercentage] = useState(
    isDiscount && totals.discountableSubtotal > 0 && initialAmount > 0
      ? String(Number(((initialAmount / totals.discountableSubtotal) * 100).toFixed(2)))
      : "",
  );
  const [error, setError] = useState("");

  const parseNominal = (value = nominal) => {
    const parsed = Number(String(value).replace(/\./g, ""));
    return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
  };

  const handleNominalChange = (event) => {
    const raw = event.target.value.replace(/\./g, "").replace(/[^0-9]/g, "");
    const amount = raw ? Number(raw) : 0;
    setNominal(raw ? amount.toLocaleString("id-ID") : "");
    setError("");

    if (isDiscount) {
      const nextPercentage =
        totals.discountableSubtotal > 0 && amount > 0
          ? Number(((amount / totals.discountableSubtotal) * 100).toFixed(2))
          : "";
      setPercentage(String(nextPercentage));
    }
  };

  const handlePercentageChange = (event) => {
    const raw = event.target.value.replace(/[^0-9.,]/g, "").replace(",", ".");
    setPercentage(raw);
    setError("");

    const parsed = Number(raw);
    if (!raw || !Number.isFinite(parsed)) {
      setNominal("");
      return;
    }

    const safePercentage = Math.min(100, Math.max(0, parsed));
    const amount = Math.round((safePercentage / 100) * totals.discountableSubtotal);
    setNominal(formatInputAmount(amount));
  };

  const saveAmount = (amount) => {
    if (isDiscount && amount > totals.discountableSubtotal) {
      setError("Diskon tidak boleh melebihi nilai dasar item");
      return;
    }

    onSave({
      discount_amount: isDiscount ? amount : totals.discountAmount,
      additional_cost: isDiscount ? totals.additionalCost : amount,
    });
    onClose();
  };

  const handleSave = () => {
    const amount = parseNominal();
    const parsedPercentage = Number(percentage);

    if (isDiscount && percentage && (!Number.isFinite(parsedPercentage) || parsedPercentage < 0 || parsedPercentage > 100)) {
      setError("Persentase diskon harus di antara 0 sampai 100 persen");
      return;
    }

    saveAmount(amount);
  };

  const itemName = item.product_name || item.item_name || item.name || "Produk";

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4 modal-backdrop-enter">
      <div
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-xl modal-panel-enter">
        <div className="flex items-center justify-between bg-teal-500 px-6 py-4">
          <h3 className="text-lg font-bold text-white">
            {isDiscount ? "Diskon Item" : "Biaya Tambahan Item"}
          </h3>
          <button type="button" onClick={onClose} className="text-white hover:text-teal-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 p-6">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="font-semibold text-slate-900">{itemName}</p>
            <div className="mt-2 flex justify-between text-sm text-slate-600">
              <span>{totals.quantity} × Rp {totals.unitPrice.toLocaleString("id-ID")}</span>
              <span className="font-semibold text-slate-800">
                Rp {totals.baseSubtotal.toLocaleString("id-ID")}
              </span>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700">
              Nominal
            </label>
            <div className="flex overflow-hidden rounded-xl border border-gray-300 focus-within:border-teal-500 focus-within:ring-2 focus-within:ring-teal-500">
              <span className="border-r border-gray-300 bg-gray-100 px-4 py-3 text-sm font-medium text-gray-500">
                Rp
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={nominal}
                onChange={handleNominalChange}
                placeholder="0"
                className="min-w-0 flex-1 bg-white px-4 py-3 text-gray-800 focus:outline-none"
              />
            </div>
          </div>

          {isDiscount && (
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Persentase
              </label>
              <div className="flex overflow-hidden rounded-xl border border-gray-300 focus-within:border-teal-500 focus-within:ring-2 focus-within:ring-teal-500">
                <input
                  type="text"
                  inputMode="decimal"
                  value={percentage}
                  onChange={handlePercentageChange}
                  placeholder="0"
                  className="min-w-0 flex-1 bg-white px-4 py-3 text-gray-800 focus:outline-none"
                />
                <span className="border-l border-gray-300 bg-gray-100 px-4 py-3 text-sm font-medium text-gray-500">
                  %
                </span>
              </div>
            </div>
          )}

          {error && <p className="text-sm font-medium text-rose-600">{error}</p>}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
          {initialAmount > 0 ? (
            <button
              type="button"
              onClick={() => saveAmount(0)}
              className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50">
              <Trash2 className="h-4 w-4" />
              Hapus
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              Batal
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="rounded-xl bg-teal-500 px-5 py-2 text-sm font-semibold text-white hover:bg-teal-600">
              Simpan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

